import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { IconPaperclip } from '@components/icons';

const EMP_LABEL = { groundlink_iii: 'Groundlink III', ryanair_dac: 'Ryanair DAC' };

export default function Comms() {
  const { t, lang } = useI18n();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId,  setOpenId]  = useState(null);

  const fmt = (d) => new Date(d).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });
  const audience = (a) => [
    a.target_employer ? EMP_LABEL[a.target_employer] : t('comms.allEmployers'),
    a.target_stations?.length ? a.target_stations.join(', ') : t('comms.allStations'),
  ].join(' · ');

  useEffect(() => {
    api.get('/comms/me').then(({ data }) => setItems(data.announcements)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const open = (a) => {
    const next = openId === a.id ? null : a.id;
    setOpenId(next);
    if (next && !a.is_read) {
      api.post(`/comms/${a.id}/read`).then(() => {
        setItems((list) => list.map((x) => (x.id === a.id ? { ...x, is_read: true } : x)));
      }).catch(() => {});
    }
  };

  return (
    <>
      <PageHeader title={t('comms.title')} subtitle={t('comms.subtitle')} />
      <div className="px-5 md:px-0 pb-6">
        {loading ? (
          <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : items.length === 0 ? (
          <div className="card p-8 text-center text-gray-500 text-sm">{t('comms.empty')}</div>
        ) : (
          <ul className="space-y-2">
            {items.map((a) => (
              <li key={a.id}>
                <div className={`card overflow-hidden ${!a.is_read ? 'border-glk-green/50' : ''}`}>
                  <button onClick={() => open(a)} className="w-full text-left px-5 py-4 flex items-start gap-3 cursor-pointer">
                    {!a.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-glk-green shrink-0" aria-label={t('comms.unread')} />}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${a.is_read ? 'font-medium text-glk-dark' : 'font-semibold text-glk-dark'} truncate`}>{a.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{fmt(a.published_at)} · {audience(a)}</p>
                    </div>
                    {a.attachments.length > 0 && <IconPaperclip size={16} className="text-gray-300 shrink-0 mt-0.5" />}
                  </button>
                  {openId === a.id && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                      className="px-5 pb-5 border-t border-gray-100">
                      <p className="text-sm text-glk-dark whitespace-pre-wrap mt-4">{a.body}</p>
                      {a.attachments.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {a.attachments.map((att) => <Attachment key={att.id} att={att} />)}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function Attachment({ att }) {
  const [imgUrl, setImgUrl] = useState('');
  const isImage = (att.content_type || '').startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    let url;
    api.get(`/comms/attachments/${att.id}`, { responseType: 'blob' })
      .then(({ data }) => { url = URL.createObjectURL(data); setImgUrl(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [att.id, isImage]);

  const openFile = async () => {
    try {
      const { data } = await api.get(`/comms/attachments/${att.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* ignore */ }
  };

  if (isImage) {
    return imgUrl
      ? <img src={imgUrl} alt={att.original_filename} className="max-w-full rounded-lg border border-gray-200" />
      : <div className="h-24 rounded-lg bg-gray-100 animate-pulse" />;
  }
  return (
    <button onClick={openFile} className="flex items-center gap-2 text-sm text-glk-green hover:underline cursor-pointer">
      <IconPaperclip size={16} />{att.original_filename || 'Anexo'}
    </button>
  );
}
